import { useMemo, useState, useEffect } from "react";
import type { CSSProperties } from "react";
import { ATTENDANCE_PRESENT, ATTENDANCE_LATE, ATTENDANCE_ABSENT } from "../constants";

type EntityStat = { total: number; active: number; completed: number; revenue: number };
type AttendanceCounts = { present: number; absent: number; late: number; excused: number; total: number; [k: string]: number };
type MonthStat = { total: number; completed: number; cancelled: number };

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

export default function ReportingDashboard({ jobs, users }: { jobs: any[]; users: any[] }) {
  const isMobile = useIsMobile();
  const stats = useMemo(() => {
    const activeJobs = jobs.filter((j: any) => j.status === "active");
    const completedJobs = jobs.filter((j: any) => j.status === "completed");
    const cancelledJobs = jobs.filter((j: any) => j.status === "cancelled");

    // Total hours worked (from completed jobs with attendance)
    const hoursWorked: Record<string, number> = {};
    const jobsWorked: Record<string, number> = {};
    completedJobs.forEach((j: any) => {
      if (!j.attendance || !j.startTime || !j.endTime) return;
      const [sh, sm] = j.startTime.split(":").map(Number);
      const [eh, em] = j.endTime.split(":").map(Number);
      let hours = (eh + em / 60) - (sh + sm / 60);
      if (hours < 0) hours += 24;

      Object.entries(j.attendance).forEach(([uid, status]) => {
        if (status === ATTENDANCE_PRESENT || status === ATTENDANCE_LATE) {
          hoursWorked[uid] = (hoursWorked[uid] || 0) + hours;
          jobsWorked[uid] = (jobsWorked[uid] || 0) + 1;
        }
      });
    });

    // Fill rate
    const totalSlots = activeJobs.reduce((sum: number, j: any) => sum + (j.slotsTotal || 0), 0);
    const filledSlots = activeJobs.reduce((sum: number, j: any) => sum + (j.signups || []).length, 0);
    const fillRate = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;

    // No-show rate
    let totalAttendees = 0;
    let noShows = 0;
    completedJobs.forEach((j: any) => {
      if (!j.attendance) return;
      Object.values(j.attendance).forEach((status) => {
        totalAttendees++;
        if (status === ATTENDANCE_ABSENT) noShows++;
      });
    });
    const noShowRate = totalAttendees > 0 ? Math.round((noShows / totalAttendees) * 100) : 0;

    // Jobs per entity
    const entityStats: Record<string, EntityStat> = {};
    jobs.forEach((j: any) => {
      const entity = j.entity || "Unassigned";
      if (!entityStats[entity]) entityStats[entity] = { total: 0, active: 0, completed: 0, revenue: 0 };
      entityStats[entity].total++;
      if (j.status === "active") entityStats[entity].active++;
      if (j.status === "completed") {
        entityStats[entity].completed++;
        if (j.startTime && j.endTime && j.payRate) {
          const [sh, sm] = j.startTime.split(":").map(Number);
          const [eh, em] = j.endTime.split(":").map(Number);
          let hours = (eh + em / 60) - (sh + sm / 60);
          if (hours < 0) hours += 24;
          const attendees = j.attendance ? Object.values(j.attendance).filter((s) => s === ATTENDANCE_PRESENT || s === ATTENDANCE_LATE).length : 0;
          entityStats[entity].revenue += hours * j.payRate * attendees;
        }
      }
    });

    // Top officers by hours
    const officerRanking = Object.entries(hoursWorked)
      .map(([uid, hours]) => {
        const user = users.find((u: any) => u.id === uid);
        return { uid, name: user?.name || "Unknown", badge: user?.badge || "", hours: Math.round(hours * 10) / 10, jobs: jobsWorked[uid] || 0 };
      })
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 10);

    // Per-officer attendance breakdown
    const attendanceByOfficer: Record<string, AttendanceCounts> = {};
    completedJobs.forEach((j: any) => {
      if (!j.attendance) return;
      Object.entries(j.attendance).forEach(([uid, status]) => {
        if (!attendanceByOfficer[uid]) attendanceByOfficer[uid] = { present: 0, absent: 0, late: 0, excused: 0, total: 0 };
        const key = String(status);
        attendanceByOfficer[uid][key] = (attendanceByOfficer[uid][key] || 0) + 1;
        attendanceByOfficer[uid].total++;
      });
    });
    const officerAttendance = Object.entries(attendanceByOfficer)
      .map(([uid, counts]) => {
        const user = users.find((u: any) => u.id === uid);
        const attended = counts.present + counts.late;
        const rate = counts.total > 0 ? Math.round((attended / counts.total) * 100) : 0;
        return { uid, name: user?.name || "Unknown", badge: user?.badge || "", ...counts, rate };
      })
      .sort((a, b) => a.rate - b.rate || b.total - a.total);

    // Monthly trend
    const monthlyJobs: Record<string, MonthStat> = {};
    jobs.forEach((j: any) => {
      if (!j.date) return;
      const month = j.date.slice(0, 7);
      if (!monthlyJobs[month]) monthlyJobs[month] = { total: 0, completed: 0, cancelled: 0 };
      monthlyJobs[month].total++;
      if (j.status === "completed") monthlyJobs[month].completed++;
      if (j.status === "cancelled") monthlyJobs[month].cancelled++;
    });

    const hasMeaningfulSample = totalSlots >= 10;

    return {
      activeCount: activeJobs.length,
      completedCount: completedJobs.length,
      cancelledCount: cancelledJobs.length,
      totalOfficers: users.filter((u: any) => u.role === "officer" && u.status === "active").length,
      fillRate,
      noShowRate,
      totalAttendees,
      hasMeaningfulSample,
      entityStats,
      officerRanking,
      officerAttendance,
      monthlyJobs,
      totalRevenue: Object.values(entityStats).reduce((sum, e) => sum + e.revenue, 0),
    };
  }, [jobs, users]);

  const sortedMonths = Object.keys(stats.monthlyJobs).sort();
  const maxMonthlyJobs = Math.max(...Object.values(stats.monthlyJobs).map((m) => m.total), 1);

  const exportReportsCSV = () => {
    const lines: string[] = [];
    // Summary section
    lines.push("SUMMARY");
    lines.push("Metric,Value");
    lines.push(`Active Jobs,${stats.activeCount}`);
    lines.push(`Completed Jobs,${stats.completedCount}`);
    lines.push(`Cancelled Jobs,${stats.cancelledCount}`);
    lines.push(`Fill Rate,${stats.fillRate}%`);
    lines.push(`No-Show Rate,${stats.noShowRate}%`);
    lines.push(`Active Officers,${stats.totalOfficers}`);
    lines.push(`Estimated Revenue,$${Math.round(stats.totalRevenue)}`);
    lines.push("");

    // Entity breakdown
    lines.push("ENTITY BREAKDOWN");
    lines.push("Entity,Total Jobs,Active,Completed,Revenue");
    Object.entries(stats.entityStats)
      .sort((a, b) => b[1].total - a[1].total)
      .forEach(([entity, data]) => {
        lines.push(`"${entity.replace(/"/g, '""')}",${data.total},${data.active},${data.completed},$${Math.round(data.revenue)}`);
      });
    lines.push("");

    // Top officers
    lines.push("TOP OFFICERS BY HOURS");
    lines.push("Rank,Officer,Badge,Hours,Jobs");
    stats.officerRanking.forEach((officer, i) => {
      lines.push(`${i + 1},"${officer.name.replace(/"/g, '""')}",${officer.badge},${officer.hours},${officer.jobs}`);
    });
    lines.push("");

    // Attendance summary
    lines.push("OFFICER ATTENDANCE SUMMARY");
    lines.push("Officer,Badge,Present,Late,Absent,Excused,Rate");
    stats.officerAttendance.forEach((officer) => {
      lines.push(`"${officer.name.replace(/"/g, '""')}",${officer.badge},${officer.present},${officer.late},${officer.absent},${officer.excused},${officer.rate}%`);
    });
    lines.push("");

    // Monthly trend
    if (sortedMonths.length > 0) {
      lines.push("MONTHLY TREND");
      lines.push("Month,Total,Completed,Cancelled");
      sortedMonths.forEach((month) => {
        const data = stats.monthlyJobs[month];
        lines.push(`${month},${data.total},${data.completed},${data.cancelled}`);
      });
    }

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reports-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0 0 20px" }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.3px" }}>Reports</h2>
        <button
          onClick={exportReportsCSV}
          style={{
            padding: "8px 16px",
            fontSize: "var(--font-base)",
            fontWeight: 600,
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            background: "var(--surface)",
            color: "var(--text)",
            cursor: "pointer",
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ ...styles.cardGrid, gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)" }}>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Active Jobs</p>
          <p style={styles.statValue}>{stats.activeCount}</p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Completed</p>
          <p style={styles.statValue}>{stats.completedCount}</p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Fill Rate</p>
          <p style={{
            ...styles.statValue,
            color: !stats.hasMeaningfulSample
              ? "var(--text)"
              : stats.fillRate >= 80 ? "var(--green)"
              : stats.fillRate >= 50 ? "var(--amber)"
              : "var(--red)",
          }}>
            {stats.fillRate}%
          </p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>No-Show Rate</p>
          <p style={{
            ...styles.statValue,
            color: stats.totalAttendees < 10
              ? "var(--text)"
              : stats.noShowRate <= 5 ? "var(--green)"
              : stats.noShowRate <= 15 ? "var(--amber)"
              : "var(--red)",
          }}>
            {stats.noShowRate}%
          </p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Active Officers</p>
          <p style={styles.statValue}>{stats.totalOfficers}</p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Est. Revenue</p>
          <p style={styles.statValue}>${Math.round(stats.totalRevenue).toLocaleString()}</p>
        </div>
      </div>

      {/* Monthly trend */}
      {sortedMonths.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Monthly Trend</h3>
          <div style={styles.chartContainer}>
            {sortedMonths.slice(-12).map((month) => {
              const data = stats.monthlyJobs[month];
              const height = (data.total / maxMonthlyJobs) * 100;
              return (
                <div key={month} style={styles.barCol}>
                  <div style={{ ...styles.barWrapper, height: isMobile ? 80 : 120 }}>
                    <div style={{ ...styles.bar, height: `${height}%` }}>
                      <span style={styles.barLabel}>{data.total}</span>
                    </div>
                  </div>
                  <span style={styles.barMonth}>{month.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <p style={{
            marginTop: 8,
            fontSize: 12,
            color: "#9ca3af",
            fontFamily: "'Fira Code', monospace",
            textAlign: "center",
          }}>
            Total jobs · last 12 months
          </p>
        </div>
      )}

      {/* Entity breakdown */}
      {Object.keys(stats.entityStats).length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Jobs by Entity</h3>
          <div style={styles.table}>
            {!isMobile && (
              <div style={styles.tableHeader}>
                <span style={{ ...styles.tableCell, flex: 2 }}>Entity</span>
                <span style={styles.tableCell}>Total</span>
                <span style={styles.tableCell}>Active</span>
                <span style={styles.tableCell}>Completed</span>
                <span style={styles.tableCell}>Revenue</span>
              </div>
            )}
            {Object.entries(stats.entityStats)
              .sort((a, b) => b[1].total - a[1].total)
              .map(([entity, data]) => (
                isMobile ? (
                  <div key={entity} style={styles.mobileCard}>
                    <div style={styles.mobileCardTitle}>{entity}</div>
                    <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Total</span><span>{data.total}</span></div>
                    <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Active</span><span>{data.active}</span></div>
                    <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Completed</span><span>{data.completed}</span></div>
                    <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Revenue</span><span>${Math.round(data.revenue).toLocaleString()}</span></div>
                  </div>
                ) : (
                  <div key={entity} style={styles.tableRow}>
                    <span style={{ ...styles.tableCell, flex: 2, fontWeight: 500 }}>{entity}</span>
                    <span style={styles.tableCell}>{data.total}</span>
                    <span style={styles.tableCell}>{data.active}</span>
                    <span style={styles.tableCell}>{data.completed}</span>
                    <span style={styles.tableCell}>${Math.round(data.revenue).toLocaleString()}</span>
                  </div>
                )
              ))}
          </div>
        </div>
      )}

      {/* Top officers */}
      {stats.officerRanking.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Top Officers by Hours</h3>
          <div style={styles.table}>
            {!isMobile && (
              <div style={styles.tableHeader}>
                <span style={styles.tableCell}>#</span>
                <span style={{ ...styles.tableCell, flex: 2 }}>Officer</span>
                <span style={styles.tableCell}>Badge</span>
                <span style={styles.tableCell}>Hours</span>
                <span style={styles.tableCell}>Jobs</span>
              </div>
            )}
            {stats.officerRanking.map((officer, i) => (
              isMobile ? (
                <div key={officer.uid} style={styles.mobileCard}>
                  <div style={styles.mobileCardTitle}>{i + 1}. {officer.name}</div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Badge</span><span>{officer.badge}</span></div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Hours</span><span>{officer.hours}h</span></div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Jobs</span><span>{officer.jobs}</span></div>
                </div>
              ) : (
                <div key={officer.uid} style={styles.tableRow}>
                  <span style={styles.tableCell}>{i + 1}</span>
                  <span style={{ ...styles.tableCell, flex: 2, fontWeight: 500 }}>{officer.name}</span>
                  <span style={styles.tableCell}>{officer.badge}</span>
                  <span style={styles.tableCell}>{officer.hours}h</span>
                  <span style={styles.tableCell}>{officer.jobs}</span>
                </div>
              )
            ))}
          </div>
        </div>
      )}
      {/* Officer Attendance Summary */}
      {stats.officerAttendance.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Officer Attendance Summary</h3>
          <div style={styles.table}>
            {!isMobile && (
              <div style={styles.tableHeader}>
                <span style={{ ...styles.tableCell, flex: 2 }}>Officer</span>
                <span style={styles.tableCell}>Badge</span>
                <span style={styles.tableCell}>Present</span>
                <span style={styles.tableCell}>Late</span>
                <span style={styles.tableCell}>Absent</span>
                <span style={styles.tableCell}>Excused</span>
                <span style={styles.tableCell}>Rate</span>
              </div>
            )}
            {stats.officerAttendance.map((officer) => (
              isMobile ? (
                <div key={officer.uid} style={styles.mobileCard}>
                  <div style={styles.mobileCardTitle}>{officer.name}</div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Badge</span><span>{officer.badge}</span></div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Present</span><span>{officer.present}</span></div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Late</span><span>{officer.late}</span></div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Absent</span><span>{officer.absent}</span></div>
                  <div style={styles.mobileCardRow}><span style={styles.mobileCardLabel}>Excused</span><span>{officer.excused}</span></div>
                  <div style={styles.mobileCardRow}>
                    <span style={styles.mobileCardLabel}>Rate</span>
                    <span style={{ fontWeight: 600, color: officer.rate >= 90 ? "var(--green)" : officer.rate >= 70 ? "var(--amber)" : "var(--red)" }}>
                      {officer.rate}%
                    </span>
                  </div>
                </div>
              ) : (
                <div key={officer.uid} style={styles.tableRow}>
                  <span style={{ ...styles.tableCell, flex: 2, fontWeight: 500 }}>{officer.name}</span>
                  <span style={styles.tableCell}>{officer.badge}</span>
                  <span style={styles.tableCell}>{officer.present}</span>
                  <span style={{ ...styles.tableCell, color: officer.late > 0 ? "var(--amber)" : undefined }}>{officer.late}</span>
                  <span style={{ ...styles.tableCell, color: officer.absent > 0 ? "var(--red)" : undefined }}>{officer.absent}</span>
                  <span style={styles.tableCell}>{officer.excused}</span>
                  <span style={{
                    ...styles.tableCell,
                    fontWeight: 700,
                    color: officer.rate >= 90 ? "var(--green)" : officer.rate >= 70 ? "var(--amber)" : "var(--red)",
                  }}>
                    {officer.rate}%
                  </span>
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    marginBottom: 24,
  },
  statCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-xl)",
    padding: 16,
    textAlign: "center",
  },
  statLabel: {
    margin: "0 0 4px",
    fontFamily: "'Fira Code', monospace",
    fontSize: "var(--font-xs)",
    fontWeight: 500,
    color: "var(--dim)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statValue: {
    margin: 0,
    fontSize: 28,
    fontWeight: 800,
    color: "var(--text)",
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: "var(--font-lg)",
    fontWeight: 700,
    margin: "0 0 12px",
    color: "var(--text)",
  },
  chartContainer: {
    display: "flex",
    alignItems: "flex-end",
    gap: 6,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-xl)",
    padding: 16,
    minHeight: 160,
  },
  barCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  barWrapper: {
    width: "100%",
    height: 120,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  bar: {
    width: "60%",
    minWidth: 20,
    background: "linear-gradient(180deg, #60a5fa, var(--accent))",
    borderRadius: "4px 4px 0 0",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    minHeight: 4,
    transition: "height 0.3s",
  },
  barLabel: {
    fontFamily: "'Fira Code', monospace",
    fontSize: 10,
    fontWeight: 500,
    color: "#fff",
    marginTop: 4,
  },
  barMonth: {
    fontFamily: "'Fira Code', monospace",
    fontSize: "var(--font-xs)",
    color: "var(--dim)",
    fontWeight: 500,
  },
  table: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-xl)",
    overflow: "hidden",
  },
  tableHeader: {
    display: "flex",
    padding: "10px 16px",
    background: "var(--surface2)",
    fontFamily: "'Fira Code', monospace",
    fontWeight: 500,
    fontSize: "var(--font-xs)",
    color: "var(--muted)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tableRow: {
    display: "flex",
    padding: "10px 16px",
    borderTop: "1px solid var(--surface3)",
    fontSize: "var(--font-base)",
    alignItems: "center",
  },
  tableCell: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    paddingRight: 8,
  },
  mobileCard: {
    padding: "12px 16px",
    borderTop: "1px solid var(--surface3)",
  },
  mobileCardTitle: {
    fontWeight: 600,
    fontSize: "var(--font-md)",
    color: "var(--text)",
    marginBottom: 6,
  },
  mobileCardRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "var(--font-base)",
    color: "var(--muted)",
    padding: "2px 0",
  },
  mobileCardLabel: {
    fontFamily: "'Fira Code', monospace",
    fontSize: "var(--font-xs)",
    color: "var(--dim)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
};
